import React from 'react'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import TradingStatsRow from './TradingStatsRow'
import { IssueEntity } from '../../../types/IssueEntity'

type TradingStatsOverviewProps = Pick<IssueEntity, 'currency' | 'tradesStats' | 'unitInstrumentSymbol'> & {
    sharesInUnit?: number | null
}

function TradingStatsOverview({
    currency,
    tradesStats,
    unitInstrumentSymbol,
    sharesInUnit,
}: TradingStatsOverviewProps) {
    const hasShareSplitAdjustments = tradesStats.some((tradeData) => tradeData?.isSplitAdjusted)
    const hasPerShareAdjustedVwap = tradesStats.some(
        (tradeData) =>
            tradeData?.totalQuantity !== null && tradeData?.vwap !== null && typeof sharesInUnit === 'number',
    )

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Tidpunkt</TableCell>
                        <TableCell>VWAP</TableCell>
                        <TableCell>Omsättning / dag ({currency})</TableCell>
                        <TableCell>Totalt antal</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tradesStats
                        .filter(({ periodType, numberOfDays }) => periodType === 'prePublication' && numberOfDays >= 10)
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.numberOfDays}
                                currency={currency}
                                row={stat}
                                timeLabel={`${stat.numberOfDays}d innan offentliggörande`}
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'publicationToTerms')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel='Mellan offentliggörande och villkor'
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'subscriptionPeriod')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel='Under teckningsperioden'
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'btuTradePeriod')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel='Under handel med BTA/BTU'
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'postIpo')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel={
                                    stat.numberOfDays === 1
                                        ? 'Första handelsdag'
                                        : `Första ${stat.numberOfDays} handelsdagarna`
                                }
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'unitsTradePeriod')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel={`Handeln i ${unitInstrumentSymbol}`}
                                sharesInUnit={
                                    typeof sharesInUnit === 'number' && sharesInUnit > 1 ? sharesInUnit : null
                                }
                            />
                        ))}
                    {(hasShareSplitAdjustments || hasPerShareAdjustedVwap) && (
                        <TableRow>
                            {hasShareSplitAdjustments && (
                                <TableCell colSpan={hasPerShareAdjustedVwap ? 1 : 4}>
                                    <Typography sx={{ fontStyle: 'italic' }}>
                                        På grund av genomförd aktiesplit under perioden har VWAP och Totalt antal
                                        justerats med hänsyn till splitten.
                                    </Typography>
                                </TableCell>
                            )}
                            {hasPerShareAdjustedVwap && (
                                <TableCell colSpan={hasShareSplitAdjustments ? 3 : 4}>
                                    <Typography align='right' sx={{ fontStyle: 'italic' }}>
                                        * = Omräknat från units till aktier
                                    </Typography>
                                </TableCell>
                            )}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default TradingStatsOverview
